import { useEffect, useRef, useState } from "react";
import { useClassPrefs } from "@/lib/smart";

// Eye centres (% of the 640x480 teacher image) for each lesson look; used for framing, blinking and the voice glow.
const FACES = {
  "/classroom/zoya-l1.jpg": [[61.4, 21.6], [66.5, 21.5]], "/classroom/zoya-l2.jpg": [[59.2, 24.9], [64.7, 24.9]],
  "/classroom/zoya-l3.jpg": [[64.5, 21.2], [69.5, 21.2]], "/classroom/zoya-l4.jpg": [[55.8, 21.6], [60.1, 21.6]],
  "/classroom/zoya-l5.jpg": [[61.3, 21.8], [66.6, 21.5]], "/classroom/zoya-l6.jpg": [[58.5, 23.0], [63.8, 22.9]],
  "/classroom/zoya-l7.jpg": [[61.3, 21.9], [66.5, 21.5]], "/classroom/zoya-l8.jpg": [[61.8, 21.9], [67.2, 21.6]],
};
const EYE_W = 2.7, EYE_H = 1.5;
let motionClips; // AI motion clips (Kling image-to-video) per teacher look; looks without clips use the simulated movement

const useSkin = (src, eyes) => {
  const [skin, setSkin] = useState(null);
  useEffect(() => {
    if (!eyes) return undefined;
    let alive = true;
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      const g = c.getContext("2d");
      g.drawImage(img, 0, 0);
      const at = ([x, y]) => {
        const d = g.getImageData(Math.round((x / 100) * c.width) - 2, Math.round(((y - 1.9) / 100) * c.height) - 1, 5, 3).data;
        const avg = [0, 1, 2].map((k) => Math.round(d.filter((_, i) => i % 4 === k).reduce((a, b) => a + b, 0) / (d.length / 4)));
        return `rgb(${avg.join(",")})`;
      };
      if (alive) setSkin(eyes.map(at));
    };
    img.src = src;
    return () => { alive = false; };
  }, [src, eyes]);
  return skin;
};

const useBlink = () => {
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    let t;
    const shut = (ms) => { setClosed(true); setTimeout(() => setClosed(false), ms); };
    const loop = () => {
      t = setTimeout(() => { shut(160); if (Math.random() < 0.2) setTimeout(() => shut(140), 330); loop(); }, 2600 + Math.random() * 3600);
    };
    loop();
    return () => clearTimeout(t);
  }, []);
  return closed;
};

const useMotionClips = () => {
  const [m, setM] = useState(motionClips || null);
  useEffect(() => {
    if (motionClips !== undefined) return;
    fetch("/classroom/motion/motion.json").then((r) => (r.ok ? r.json() : null)).catch(() => null)
      .then((x) => { motionClips = x; setM(x); });
  }, []);
  return m;
};

export const LiveZoya = ({ src, speaking, levelRef, portrait = true, greeting = false, className = "", testid = "live-zoya" }) => {
  const root = useRef(null);
  const eyes = FACES[src];
  const skin = useSkin(src, eyes);
  const closed = useBlink();
  const clips = useMotionClips();
  const lowbw = useClassPrefs((x) => x.lowbw);

  useEffect(() => {
    let raf;
    const tick = () => {
      const lv = speaking ? (levelRef?.current || 0.3 + 0.22 * Math.abs(Math.sin(Date.now() / 95))) : 0;
      root.current?.style.setProperty("--amp", lv.toFixed(3));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [speaking, levelRef]);

  const fx = eyes ? (eyes[0][0] + eyes[1][0]) / 2 : 50;
  const fy = eyes ? (eyes[0][1] + eyes[1][1]) / 2 + 4 : 40;
  const zoom = portrait && eyes ? 2.1 : 1;
  const frame = zoom > 1 ? { transform: `translate(${50 - fx}%, ${42 - fy}%) scale(${zoom})`, transformOrigin: `${fx}% ${fy}%` } : {};
  const look = !lowbw && clips?.looks?.[src];
  const video = look && `${look}-${greeting ? "wave" : speaking ? "explain" : "idle"}`;

  return (
    <div ref={root} className={`relative overflow-hidden aspect-[4/3] bg-[#D6E6FA] ${className}`} style={{ "--amp": 0 }} data-testid={testid} data-speaking={speaking ? "true" : "false"}>
      {video ? (
        <video key={video} autoPlay loop muted playsInline poster={src} className="absolute inset-0 w-full h-full object-cover" data-testid={`${testid}-motion`}>
          <source src={`${video}.mp4`} type="video/mp4" />
          <source src={`${video}.webm`} type="video/webm" />
        </video>
      ) : (
        <div className="absolute inset-0" style={frame}>
          <div className="absolute inset-0 rs-zoya-sway" style={{ transformOrigin: `${fx}% ${fy + 14}%` }}>
            <div className="absolute inset-0 rs-zoya-breathe" style={{ transformOrigin: `${fx}% 100%` }}>
              <div className="absolute inset-0" style={{ transform: "translateY(calc(var(--amp) * -0.35%)) rotate(calc(var(--amp) * 0.5deg))", transformOrigin: `${fx}% ${fy + 10}%` }}>
                <img src={src} alt="Zoya Ma'am" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
                {eyes && skin && eyes.map(([x, y], i) => (
                  <span key={i} aria-hidden className="absolute rounded-[50%]" data-testid={`${testid}-eyelid-${i}`}
                    style={{ left: `${x - EYE_W / 2}%`, top: `${y - EYE_H * 0.62}%`, width: `${EYE_W}%`, height: `${EYE_H}%`, background: skin[i],
                      transform: `scaleY(${closed ? 1 : 0})`, transformOrigin: "top", transition: "transform 70ms ease-in",
                      filter: "blur(0.35px)", boxShadow: "inset 0 -1px 0 rgba(40,20,15,0.6)" }} />
                ))}
                {eyes && <span aria-hidden className="absolute rounded-[50%] pointer-events-none"
                  style={{ left: `${fx - 2.2}%`, top: `${fy + 2.6}%`, width: "4.4%", height: "2.4%", opacity: "calc(var(--amp) * 1.1)",
                    background: "radial-gradient(closest-side, rgba(255,140,170,0.75), rgba(0,229,255,0.25) 60%, transparent)", filter: "blur(0.8px)", mixBlendMode: "screen" }} />}
              </div>
            </div>
          </div>
        </div>
      )}
      <div aria-hidden className="absolute inset-0 pointer-events-none rounded-[inherit]"
        style={{ boxShadow: "inset 0 0 0 calc(var(--amp) * 5px) rgba(0,229,255,0.8), inset 0 0 calc(var(--amp) * 40px) rgba(0,229,255,0.45)" }} />
    </div>
  );
};
